import type { CSSProperties } from "react";

import { Icon } from "@core/components/Icon";
import { LinkBehaviour } from "@core/components/LinkBehaviour";
import { PictureBehaviour, type PictureSource } from "@core/components/PictureBehaviour";
import { DESKTOP_MEDIA_QUERY, TABLET_MEDIA_QUERY } from "@core/consts/breakpoints";
import { type SafeHtmlAttributes } from "@core/types/SafeHtmlAttributes";
import classNames from "classnames";

import { ChevronForwardIcon } from "@jti/brand-icons/chevron-forward";

import styles from "./PromoCardHorizontal.module.scss";

export type PromoCardHorizontalProps = {
  title: string;
  description?: string;
  imageSrc: string;
  imageSrcTablet?: string;
  imageSrcDesktop?: string;
  imageAlt?: string;
  imageContainerBackgroundColor?: string;
  imageContainerBackgroundColorTablet?: string;
  imageContainerBackgroundColorDesktop?: string;
  href: string;
  nativeBehaviour?: boolean;
} & SafeHtmlAttributes<HTMLAnchorElement>;

export const PromoCardHorizontal = ({
  title,
  description,
  imageSrc,
  imageSrcTablet,
  imageSrcDesktop,
  imageAlt = "",
  imageContainerBackgroundColor = "#000",
  imageContainerBackgroundColorTablet,
  imageContainerBackgroundColorDesktop,
  href,
  nativeBehaviour,
  ...rest
}: PromoCardHorizontalProps) => {
  const sources: PictureSource[] = [
    ...(imageSrcDesktop ? [{ srcSet: imageSrcDesktop, media: DESKTOP_MEDIA_QUERY }] : []),
    ...(imageSrcTablet ? [{ srcSet: imageSrcTablet, media: TABLET_MEDIA_QUERY }] : []),
  ];

  const backgroundColorVariables: CSSProperties = {
    ...(imageContainerBackgroundColor && { "--promo-card-bg": imageContainerBackgroundColor }),
    ...(imageContainerBackgroundColorTablet && { "--promo-card-bg-tablet": imageContainerBackgroundColorTablet }),
    ...(imageContainerBackgroundColorDesktop && { "--promo-card-bg-desktop": imageContainerBackgroundColorDesktop }),
  };

  return (
    <LinkBehaviour
      {...rest}
      href={href}
      nativeBehaviour={nativeBehaviour}
      className={classNames("light-theme", styles.card)}
    >
      <div className={styles.contentWrapper} style={backgroundColorVariables}>
        <div className={styles.text}>
          <p className={styles.title}>{title}</p>
          {description && <p className={styles.description}>{description}</p>}
        </div>
        <Icon icon={ChevronForwardIcon} size="sm" className={styles.icon} aria-hidden="true" />
      </div>
      <div className={styles.imageContainer}>
        <PictureBehaviour
          className={styles.picture}
          sources={sources}
          imageProps={{ src: imageSrc, alt: imageAlt, className: styles.image }}
          nativeBehaviour={nativeBehaviour}
        />
      </div>
    </LinkBehaviour>
  );
};
